import React, { useMemo } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, ButtonVariant } from '../../common/Button'
import { FormControl } from '../../common/FormControl'
import { HorizontalFormSection } from '../../common/HorizontalFormSection'
import { IconChoices } from '../../common/Icon'
import { TextInput } from '../../common/TextInput'
import { ToastType } from '../../common/Toast/toast-type'
import {
  createUpdateEmailSchema,
  UpdateEmailFormValues,
} from '../../../forms/schemas/update_email'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useAuthenticatedUserId } from '../../../hooks/useAuthenticatedUserId'
import { useFormErrorMessage } from '../../../hooks/useFormErrorMessage'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { updateEmail, userQueries } from '../../../api/users'
import { isRateLimited } from '../../../helpers/rate_limit'

export const ChangeEmail = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { getErrorMessage } = useFormErrorMessage()

  const userId = useAuthenticatedUserId() as string
  const { data: user } = useQuery(userQueries.getUser(userId))

  const getDefaultValues = (): UpdateEmailFormValues => {
    return {
      currentPassword: '',
      newEmail: '',
      newEmailConfirmation: '',
    }
  }

  const updateEmailSchema = useMemo(
    () => createUpdateEmailSchema(user?.email),
    [user?.email]
  )

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(updateEmailSchema),
    defaultValues: getDefaultValues(),
  })

  const { mutate: mutateUpdateEmail, isPending: isUpdatingEmail } = useMutation(
    {
      mutationFn: (variables: { email: string; password: string }) =>
        updateEmail(variables.email, variables.password),
      onSuccess: () => {
        setToastNotification(
          ToastType.SUCCESS,
          translate('changeEmail.flashMessage.updateEmailSuccess')
        )
        reset()
      },
      onError: (error) => {
        setToastNotification(
          ToastType.DANGER,
          translate(
            isRateLimited(error)
              ? 'flashMessage.tooManyAttemptsError'
              : 'changeEmail.flashMessage.updateEmailFailure'
          )
        )
      },
    }
  )
  const onSubmit = async (data: UpdateEmailFormValues): Promise<void> => {
    mutateUpdateEmail({ email: data.newEmail, password: data.currentPassword })
  }

  const onError = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const loading = isUpdatingEmail

  return (
    <>
      {user && (
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <HorizontalFormSection
            title={translate('changeEmail.title')}
            subtitle={translate('changeEmail.text')}
            icon={IconChoices.EMAIL}
            iconPlacement="left"
            borderBottom
          >
            <FormControl
              name="email"
              label={translate('changeEmail.labels.currentEmail')}
              disabled
            >
              <TextInput
                value={user.email}
                onChange={() => null}
                onBlur={() => null}
                disabled
                type="email"
              />
            </FormControl>
            <Controller
              control={control}
              render={({ field: { value, name, onChange, onBlur } }) => (
                <FormControl
                  label={translate('changeEmail.labels.newEmail')}
                  error={errors.newEmail && getErrorMessage(errors.newEmail)}
                  name={name}
                >
                  <TextInput
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!errors.newEmail}
                    type="email"
                    autoComplete="new-email"
                  />
                </FormControl>
              )}
              name="newEmail"
            />
            <Controller
              control={control}
              render={({ field: { value, name, onChange, onBlur } }) => (
                <FormControl
                  label={translate('changeEmail.labels.repeatNewEmail')}
                  error={
                    errors.newEmailConfirmation &&
                    getErrorMessage(errors.newEmailConfirmation)
                  }
                  name={name}
                >
                  <TextInput
                    autoComplete="new-email"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!errors.newEmailConfirmation}
                    type="email"
                  />
                </FormControl>
              )}
              name="newEmailConfirmation"
            />
            <Controller
              control={control}
              render={({ field: { value, name, onChange, onBlur } }) => (
                <FormControl
                  label={translate('common.form.labels.password')}
                  error={
                    errors.currentPassword &&
                    getErrorMessage(errors.currentPassword)
                  }
                  name={name}
                >
                  <TextInput
                    autoComplete="new-password"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!errors.currentPassword}
                    type="password"
                  />
                </FormControl>
              )}
              name="currentPassword"
            />
            <div className="mt-4 flex justify-end">
              <Button
                label={translate('common.actions.saveChanges')}
                type="submit"
                loading={loading}
                disabled={loading || !isValid}
                variant={ButtonVariant.PRIMARY}
              />
            </div>
          </HorizontalFormSection>
        </form>
      )}
    </>
  )
}
